import { useEffect, useRef, useState } from 'react'

/**
 * Semnătura pe ecran.
 *
 * Se desenează cu Pointer Events, cu Touch Events ca plasă de siguranță: pe 22.09.2026, la
 * standul din farmacie, pe un iPhone nu apărea nicio urmă — degetul trăgea pe chenar și pânza
 * rămânea albă, iar butonul de trimitere nu se aprindea niciodată. `touch-action: none` oprește
 * scroll-ul paginii cât timp degetul e pe pânză.
 *
 * Pânza are rezoluție dublă față de dimensiunea afișată (`devicePixelRatio`), ca PNG-ul ajuns
 * în procesul-verbal să nu fie o mâzgăleală pixelată la tipărire.
 *
 * Trei apărări, fiindcă în teren nu se poate depana:
 *
 *   1. **Transformarea se pune la fiecare linie**, nu o dată la măsurare. Orice lucru care
 *      resetează pânza — o reajustare de dimensiune, o pierdere de context, pe care WebKit o
 *      face sub presiune de memorie — lăsa altfel desenul să plece în afara cadrului, invizibil.
 *   2. **Touch Events ca rezervă.** Dacă `pointerdown` nu ajunge deloc, desenăm din `touchstart`.
 *      Cele două nu se calcă: prima sursă care răspunde o oprește pe cealaltă.
 *   3. **Un punct la simpla atingere.** Vezi imediat că pânza e vie, chiar înainte să tragi.
 */
interface Props {
  /** Primește data URI PNG, sau `null` când pânza e goală. */
  onChange: (dataUri: string | null) => void
  disabled?: boolean
}

/** Ce sursă de evenimente a răspuns prima. A doua se dezactivează, ca linia să nu se dubleze. */
type Sursa = 'pointer' | 'touch' | null

export function SemnaturaPad({ onChange, disabled }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const deseneaza = useRef(false)
  /** A atins pânza ȘI a mișcat. Ține minte între randări, spre deosebire de state. */
  const aTras = useRef(false)
  const sursa = useRef<Sursa>(null)
  /** Raportul cu care e întinsă pânza. Se pune înaintea fiecărei linii. */
  const ratie = useRef(1)
  const [areSemnatura, setAreSemnatura] = useState(false)

  /**
   * Măsurarea pânzei, refăcută ori de câte ori se schimbă dimensiunea afișată.
   *
   * O singură măsurare la montare nu ajunge: dacă layout-ul se schimbă după aceea — telefonul
   * rotit, tastatura care apare, un reflow târziu — backing store-ul rămâne la dimensiunea
   * veche, iar linia apare decalată față de deget. S-a văzut la proba din producție: pânza
   * afișată avea 393 px, iar backing store-ul rămăsese la 448.
   */
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let latimeVeche = 0
    let inaltimeVeche = 0

    const masoara = (fortat = false) => {
      const rect = canvas.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return
      if (!fortat && rect.width === latimeVeche && rect.height === inaltimeVeche) return

      // Ce era desenat se păstrează: cine a semnat deja n-are de ce s-o ia de la capăt
      // fiindcă a rotit telefonul.
      const vechi = latimeVeche > 0 ? canvas.toDataURL('image/png') : null

      const r = Math.min(window.devicePixelRatio || 1, 3)
      ratie.current = r
      canvas.width = Math.round(rect.width * r)
      canvas.height = Math.round(rect.height * r)
      latimeVeche = rect.width
      inaltimeVeche = rect.height

      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.setTransform(r, 0, 0, r, 0, 0)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, rect.width, rect.height)

      if (vechi) {
        const img = new Image()
        img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height)
        img.src = vechi
      }
    }

    masoara()
    const observer = new ResizeObserver(() => masoara())
    observer.observe(canvas)

    /*
      WebKit poate arunca conținutul pânzei sub presiune de memorie. Când o face, tot ce desenezi
      după aceea se duce în gol, fără nicio eroare. Refacem pânza la revenire.
    */
    const laPierdere = (e: Event) => e.preventDefault()
    const laRevenire = () => masoara(true)
    canvas.addEventListener('contextlost', laPierdere)
    canvas.addEventListener('contextrestored', laRevenire)

    return () => {
      observer.disconnect()
      canvas.removeEventListener('contextlost', laPierdere)
      canvas.removeEventListener('contextrestored', laRevenire)
    }
  }, [])

  /**
   * Contextul, gata de desenat: cu creionul pus și cu transformarea la zi.
   *
   * Se cere la FIECARE linie. Atributele `width`/`height` ale pânzei resetează starea
   * contextului, iar o repunere la măsurare nu mai ajunge dacă între timp s-a măsurat din nou.
   */
  function creion(): CanvasRenderingContext2D | null {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return null
    /*
      Pânza n-a fost măsurată niciodată: backing store-ul e la implicitul de 300×150, iar ce
      desenezi apare în altă parte decât degetul. Se măsoară acum, la prima atingere.
    */
    const rect = canvas.getBoundingClientRect()
    const asteptat = Math.round(rect.width * ratie.current)
    if (rect.width > 0 && canvas.width !== asteptat) {
      const r = Math.min(window.devicePixelRatio || 1, 3)
      ratie.current = r
      canvas.width = Math.round(rect.width * r)
      canvas.height = Math.round(rect.height * r)
      ctx.setTransform(r, 0, 0, r, 0, 0)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, rect.width, rect.height)
    }
    ctx.setTransform(ratie.current, 0, 0, ratie.current, 0, 0)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#111827'
    return ctx
  }

  function pozitie(clientX: number, clientY: number) {
    const rect = canvasRef.current!.getBoundingClientRect()
    return { x: clientX - rect.left, y: clientY - rect.top }
  }

  function incepe(clientX: number, clientY: number) {
    if (disabled) return
    const ctx = creion()
    if (!ctx) return
    const p = pozitie(clientX, clientY)
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
    /*
      Un punct chiar la atingere. Nu e semnătură — `aTras` rămâne fals, deci butonul nu se
      aprinde —, dar se vede pe loc că pânza răspunde. Fără el, cine atingea și nu mișca destul
      credea că ecranul e mort.
    */
    ctx.lineTo(p.x + 0.01, p.y)
    ctx.stroke()
    deseneaza.current = true
  }

  function continua(clientX: number, clientY: number) {
    if (!deseneaza.current || disabled) return
    const ctx = creion()
    if (!ctx) return
    const p = pozitie(clientX, clientY)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
    aTras.current = true
    if (!areSemnatura) setAreSemnatura(true)
  }

  function opreste() {
    if (!deseneaza.current) return
    deseneaza.current = false
    const canvas = canvasRef.current
    if (!canvas) return
    // O atingere fără mișcare NU e semnătură. Fără condiția asta, o simplă apăsare pe pânză
    // trimitea un PNG alb, butonul se activa, iar predarea se putea confirma nesemnată —
    // serverul n-are cum să distingă un PNG gol de unul cu desen.
    if (!aTras.current) return
    onChange(canvas.toDataURL('image/png'))
  }

  // --- Pointer Events: calea normală -------------------------------------------------------

  function pointerJos(e: React.PointerEvent<HTMLCanvasElement>) {
    if (sursa.current === 'touch') return
    sursa.current = 'pointer'
    // Captura poate lipsi (pointer deja eliberat, sau un eveniment sintetic). Nu e motiv
    // să pice tot handler-ul și să rămână pânza moartă.
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* fără captură; desenul merge oricum */
    }
    incepe(e.clientX, e.clientY)
  }

  function pointerMisca(e: React.PointerEvent<HTMLCanvasElement>) {
    if (sursa.current === 'touch') return
    continua(e.clientX, e.clientY)
  }

  // --- Touch Events: plasa de siguranță ----------------------------------------------------
  //
  // Se folosesc DOAR dacă `pointerdown` n-a ajuns niciodată. Pe telefoanele unde totul e în
  // regulă, `sursa` devine `pointer` la prima atingere și ramura asta nu mai rulează.

  function touchJos(e: React.TouchEvent<HTMLCanvasElement>) {
    if (sursa.current === 'pointer') return
    sursa.current = 'touch'
    const t = e.touches[0]
    if (t) incepe(t.clientX, t.clientY)
  }

  function touchMisca(e: React.TouchEvent<HTMLCanvasElement>) {
    if (sursa.current !== 'touch') return
    const t = e.touches[0]
    if (t) continua(t.clientX, t.clientY)
  }

  function sterge() {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const rect = canvas.getBoundingClientRect()
    ctx.setTransform(ratie.current, 0, 0, ratie.current, 0, 0)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, rect.width, rect.height)
    aTras.current = false
    deseneaza.current = false
    setAreSemnatura(false)
    onChange(null)
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-b4l-text">Semnătura</span>
        {areSemnatura && (
          <button
            type="button"
            onClick={sterge}
            className="text-sm text-b4l-blue underline"
            disabled={disabled}
          >
            Șterge și reia
          </button>
        )}
      </div>
      <canvas
        ref={canvasRef}
        onPointerDown={pointerJos}
        onPointerMove={pointerMisca}
        onPointerUp={opreste}
        onPointerCancel={opreste}
        onTouchStart={touchJos}
        onTouchMove={touchMisca}
        onTouchEnd={opreste}
        onTouchCancel={opreste}
        style={{ touchAction: 'none' }}
        className="mt-1 h-40 w-full rounded-lg border-2 border-dashed border-b4l-border bg-white"
      />
      {!areSemnatura && (
        <p className="mt-1 text-xs text-b4l-text-muted">
          Semnează cu degetul în chenarul de mai sus.
        </p>
      )}
    </div>
  )
}
